import i18n, { type Resource } from "i18next"
import { initReactI18next } from "react-i18next"
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  detectBrowserLocale,
  isSupportedLocale,
  type SupportedLocale,
} from "./locales"

const localeModules = import.meta.glob("./locales/*/*.json", { eager: true }) as Record<
  string,
  Record<string, unknown>
>

const resources: Record<string, Record<string, unknown>> = {}

for (const [filePath, module] of Object.entries(localeModules)) {
  const match = filePath.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/)
  if (!match) continue
  const [, lng, nsFile] = match
  const ns = nsFile.replace(/\.json$/, "")
  if (!resources[lng]) resources[lng] = {}
  // Prefer default export. Eager JSON glob modules are `{ default: {...} }`;
  // using the module namespace drops reserved top-level keys like `import`.
  const mod = module as { default?: Record<string, unknown> } & Record<string, unknown>
  resources[lng][ns] = (mod.default ?? mod) as Record<string, unknown>
}

const namespaces = Object.keys(resources.en ?? {})

function readStoredLocale(): SupportedLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    return isSupportedLocale(stored) ? stored : null
  } catch {
    return null
  }
}

export function getInitialLocale(userLocale?: string | null): SupportedLocale {
  return readStoredLocale() ?? (isSupportedLocale(userLocale) ? userLocale : detectBrowserLocale())
}

export function persistLocale(locale: SupportedLocale) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    /* ignore storage errors */
  }
}

export async function initI18n(initialLocale?: SupportedLocale) {
  const locale = initialLocale ?? getInitialLocale()

  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      resources: resources as Resource,
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      defaultNS: "common",
      ns: namespaces,
      interpolation: { escapeValue: false },
    })
  } else if (i18n.language !== locale) {
    await i18n.changeLanguage(locale)
  }

  persistLocale(locale)
  return i18n
}

export async function setAppLocale(locale: SupportedLocale) {
  persistLocale(locale)
  await i18n.changeLanguage(locale)
}

export function getAppLocale(): SupportedLocale {
  return isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE
}

export default i18n
